import { useCallback } from "react";

import { useOrderDetailV2 } from "../OrderDetailV2Context";
import type { ActionsConfigMap } from "../types";

/**
 * Hook that centralises action execution for OrderDetailV2 components.
 *
 * Components receive an `ActionsConfigMap` (actionId → config) from the
 * transformer and pass it here.  The hook reads shared dependencies
 * (`bookingId`, `openUrl`, `orderActionHandlers`, `showActionModal`) from
 * context so individual components never need to know about them.
 *
 * Supported action types:
 *   - OPEN_URL       – opens a URL (supports {{bookingId}} interpolation)
 *   - API_EXECUTED   – calls a registered handler, shows a modal from the
 *                      API response `data.components[0].data`
 */
export function useOrderActions(actionsConfig: ActionsConfigMap = {}) {
  const { bookingId, orderActionHandlers, showActionModal, openUrl } =
    useOrderDetailV2();

  const executeAction = useCallback(
    (actionId: string) => {
      const config = actionsConfig[actionId];
      if (!config) return;

      const type = String(config.type || "").toUpperCase();

      if (type === "OPEN_URL") {
        const url = (config.url || "").replace(
          /\{\{bookingId\}\}/g,
          bookingId || ""
        );
        openUrl?.(url, config.openInNewTab !== false);
      } else if (type === "API_EXECUTED") {
        const handler = orderActionHandlers?.[actionId];
        if (!handler) return;
        handler({ bookingId: bookingId || "" })
          .then((response) => {
            if (!response) return;
            const componentData = response.data?.components?.[0]?.data;
            if (componentData) {
              const viewType = response.success ? "success" : "error";
              showActionModal?.({ componentData, viewType });
            }
          })
          .catch((error: any) => {
            const componentData =
              error?.response?.data?.data?.components?.[0]?.data;
            if (componentData) {
              showActionModal?.({ componentData, viewType: "error" });
            }
          });
      }
    },
    [actionsConfig, bookingId, orderActionHandlers, showActionModal, openUrl]
  );

  return { executeAction };
}
